/**
 * usePublishValidation
 *
 * Hook que centraliza a rotina de publicação de agente:
 *   1. Valida graph (nodes/edges/llm_config) via validateGraph
 *   2. Verifica recursos PRO (nodes PRO, áudios PRO, websites PRO) se cliente
 *      não tem plano PRO
 *   3. Chama api.publishAgent
 *   4. Trata erros conhecidos (requires_payment, non_field_errors, etc.) com
 *      modais Swal padronizados
 *
 * Antes deste hook, a lógica estava duplicada entre:
 *   - CanvasInner.js (validateBeforeAction + handlePublish)
 *   - PublishChannelModal.js (checkProNodes + handleDoPublish)
 *
 * Agora os 3 consumidores (canvas, publish modal, aba Canais) compartilham
 * a mesma fonte. Ajustes de copy, novos modais ou novos checks PRO entram
 * em UM lugar só.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAgentsFlow } from '../contexts/AgentsFlowContext';
import { useAgentsFlowApi } from './useAgentsFlowApi';
import useProFeature, { PRO_SIDEBAR_KEYS } from './useProFeature';
import { validateGraph, formatErrorsForDisplay } from '../utils/graphValidation';
import AgentsFlowService from '../services/AgentsFlowService';

/**
 * Hook compartilhado.
 *
 * Uso:
 *   const { validateAndPublish, publishWithoutValidation } = usePublishValidation();
 *   const result = await validateAndPublish(agentId, nodes, edges, {
 *     onLLMValidationFail: () => setShowEditorPanel(true), // opcional
 *   });
 *   // result: { success, error?, response? }
 */
export default function usePublishValidation() {
  const navigate = useNavigate();
  const { state, updateAgent: updateAgentState } = useAgentsFlow();
  const { editor } = state;
  const api = useAgentsFlowApi();
  const { isProPlan } = useProFeature();

  /**
   * Validação completa: graph + PRO gating.
   *
   * Se `nodes`/`edges` forem null/undefined, busca o graph SALVO via API.
   * Útil para callers que não têm acesso ao state do canvas (ex: ChannelsTab
   * dentro do AgentEditorPanel — o canvas está montado mas seu state é
   * inacessível por aqui).
   *
   * @returns {Promise<{valid: boolean, reason?: string}>}
   *   reason: 'invalid_graph' | 'llm_missing' | 'pro_required' | 'no_id' | 'graph_fetch_error'
   */
  const validate = useCallback(
    async (agentId, nodes, edges, options = {}) => {
      if (!agentId) return { valid: false, reason: 'no_id' };

      // Fallback: buscar graph salvo se caller não passou
      let resolvedNodes = nodes;
      let resolvedEdges = edges;
      if (resolvedNodes == null || resolvedEdges == null) {
        try {
          const graphResp = await AgentsFlowService.getGraph(agentId);
          const graph = graphResp.data || {};
          resolvedNodes = graph.nodes || [];
          resolvedEdges = graph.edges || [];
        } catch (err) {
          console.error('[usePublishValidation] Erro ao buscar graph:', err);
          Swal.fire({
            icon: 'error',
            title: 'Erro ao validar fluxo',
            text: 'Não foi possível ler o fluxo do agente. Tente novamente.',
            confirmButtonColor: '#3b82f6',
          });
          return { valid: false, reason: 'graph_fetch_error' };
        }
      }

      // 1. Graph validation (LLM, nodes, edges)
      const validation = validateGraph(resolvedNodes, resolvedEdges, {
        llm_config: editor.llm_config,
      });

      if (!validation.canPublish) {
        if (!validation.results.llm.isValid) {
          const result = await Swal.fire({
            icon: 'warning',
            title: 'Configuração Incompleta',
            html: `
              <p>Para publicar o agente, você precisa configurar o modelo de IA.</p>
              <p style="margin-top: 1rem;">Acesse <strong>Configurações > LLM</strong>
              para selecionar um provedor e modelo.</p>
            `,
            confirmButtonText: 'Ir para Configurações',
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#3b82f6',
          });
          if (result.isConfirmed && options.onLLMValidationFail) {
            options.onLLMValidationFail();
          }
          return { valid: false, reason: 'llm_missing' };
        }

        Swal.fire({
          icon: 'warning',
          title: 'Validação do Fluxo',
          html: formatErrorsForDisplay(validation.errors),
          confirmButtonText: 'Entendi',
          confirmButtonColor: '#3b82f6',
        });
        return { valid: false, reason: 'invalid_graph' };
      }

      // 2. PRO plan gating
      if (!isProPlan) {
        const proFeatures = [];

        // PRO nodes no fluxo
        const proNodes = (resolvedNodes || []).filter(
          (n) => n.data?.pro === true || PRO_SIDEBAR_KEYS.includes(n.data?.key),
        );
        const uniqueNodeLabels = [
          ...new Set(proNodes.map((n) => n.data?.label || n.data?.key)),
        ];
        proFeatures.push(...uniqueNodeLabels);

        // Áudios PRO na galeria
        try {
          const mediaResponse = await AgentsFlowService.listMedia(agentId);
          const mediaList = mediaResponse.data || [];
          if (mediaList.some((m) => m.media_type === 'audio')) {
            proFeatures.push('Áudios na Galeria de Mídias');
          }
        } catch (err) {
          console.warn('[usePublishValidation] Erro ao verificar mídias PRO:', err);
        }

        // Websites PRO na base de conhecimento
        try {
          const knowledgeResponse = await AgentsFlowService.getKnowledge(agentId);
          const knowledgeList = knowledgeResponse.data || [];
          if (knowledgeList.some((k) => k.source_type === 'website')) {
            proFeatures.push('Websites na Base de Conhecimento');
          }
        } catch (err) {
          console.warn('[usePublishValidation] Erro ao verificar knowledge PRO:', err);
        }

        if (proFeatures.length > 0) {
          const listHtml = proFeatures.map((l) => `<li>${l}</li>`).join('');
          const result = await Swal.fire({
            icon: 'info',
            title: 'Recursos PRO no Fluxo',
            html: `
              <p>Seu agente contém recursos PRO disponíveis apenas no plano <strong>Business</strong>:</p>
              <ul style="text-align:left;margin:12px auto;max-width:260px;">${listHtml}</ul>
              <p>Faça upgrade do seu plano para liberar todos os recursos.</p>
            `,
            confirmButtonText: 'Ver Planos',
            showCancelButton: true,
            cancelButtonText: 'Voltar',
            confirmButtonColor: '#8b5cf6',
          });
          if (result.isConfirmed) navigate('/my-plan');
          return { valid: false, reason: 'pro_required' };
        }
      }

      return { valid: true };
    },
    [editor.llm_config, isProPlan, navigate],
  );

  /**
   * Trata erro de publish com modais padronizados.
   * Retorna { handled: bool } — true se exibimos modal customizado para o erro.
   */
  const handlePublishError = useCallback(
    (error) => {
      const data = error?.response?.data;

      if (data?.requires_payment) {
        Swal.fire({
          icon: 'info',
          title: 'Limite de Agentes Publicados',
          html: `
            <div style="text-align: left;">
              <p>${data.error || 'Você atingiu o limite de agentes publicados do seu plano.'}</p>
              <p><strong>Uso atual:</strong> ${data.current_usage}/${data.total_limit} agentes</p>
              <p><strong>Valor por agente adicional:</strong> R$ ${data.extra_value}</p>
              <div style="background: #e3f2fd; border: 1px solid #2196f3; border-radius: 8px; padding: 16px; margin: 16px 0;">
                <p style="color: #1976d2; font-size: 14px; margin: 0;">
                  <strong>💡 Solução:</strong> Contrate agentes adicionais para continuar publicando.
                </p>
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: '🚀 Contratar Agora',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: 'var(--primary)',
          cancelButtonColor: '#6c757d',
        }).then((result) => {
          if (result.isConfirmed) {
            navigate('/subscriptions');
          }
        });
        return { handled: true };
      }

      // Mensagem genérica derivada do payload
      let msg = 'Ocorreu um erro ao publicar o agente. Tente novamente.';
      if (data) {
        if (data.non_field_errors && Array.isArray(data.non_field_errors)) {
          msg = data.non_field_errors.join('\n');
        } else if (data.detail) {
          msg = data.detail;
        } else if (data.message) {
          msg = data.message;
        } else if (data.error) {
          msg = data.error;
        }
      }
      Swal.fire({
        icon: 'error',
        title: 'Erro na Publicação',
        text: msg,
        confirmButtonText: 'Entendi',
        confirmButtonColor: '#3b82f6',
      });
      return { handled: true };
    },
    [navigate],
  );

  /**
   * Validação + publish completos. É o método principal.
   *
   * @returns {Promise<{success: boolean, response?: any, reason?: string}>}
   *
   *   success: true → publish OK
   *   success: false + reason='invalid_graph'|'llm_missing'|'pro_required' → bloqueado pré-API
   *   success: false + reason='api_error' → API falhou (erro já mostrado em modal)
   */
  const validateAndPublish = useCallback(
    async (agentId, nodes, edges, options = {}) => {
      const v = await validate(agentId, nodes, edges, options);
      if (!v.valid) return { success: false, reason: v.reason };

      try {
        const response = await api.publishAgent(agentId);
        // Defensivo: backend pode não devolver is_published no body. Sem isso,
        // o context não atualiza e o overlay de gate não some. Forçamos aqui.
        if (updateAgentState) {
          updateAgentState({ id: agentId, is_published: true, is_active: true });
        }
        if (options.silent !== true) {
          Swal.fire({
            icon: 'success',
            title: 'Agente Publicado!',
            text: 'Seu agente foi publicado com sucesso e está pronto para uso.',
            timer: 2500,
            showConfirmButton: false,
          });
        }
        return { success: true, response };
      } catch (error) {
        console.error('[usePublishValidation] Erro ao publicar:', error);
        handlePublishError(error);
        return { success: false, reason: 'api_error', error };
      }
    },
    [validate, api, handlePublishError, updateAgentState],
  );

  /**
   * Publica sem validação prévia. Usado quando caller já validou via outro
   * caminho e só quer reaproveitar o tratamento de erro.
   */
  const publishWithoutValidation = useCallback(
    async (agentId, options = {}) => {
      try {
        const response = await api.publishAgent(agentId);
        if (updateAgentState) {
          updateAgentState({ id: agentId, is_published: true, is_active: true });
        }
        if (options.silent !== true) {
          Swal.fire({
            icon: 'success',
            title: 'Agente Publicado!',
            text: 'Seu agente foi publicado com sucesso e está pronto para uso.',
            timer: 2500,
            showConfirmButton: false,
          });
        }
        return { success: true, response };
      } catch (error) {
        handlePublishError(error);
        return { success: false, reason: 'api_error', error };
      }
    },
    [api, handlePublishError, updateAgentState],
  );

  return {
    validate,
    validateAndPublish,
    publishWithoutValidation,
    handlePublishError,
  };
}
